import { useState, useEffect, useMemo, useCallback } from 'react';
import SocialActions from '../../components/SocialActions/SocialActions';
import ContentEditorModal from '../../components/ContentEditorModal/ContentEditorModal';
import { useAuth } from '../../contexts/AuthContext';

function Mahlet() {
  const { user } = useAuth();
  const [hymnsList, setHymnsList] = useState([]);
  const [selected, setSelected] = useState('');
  const [hymn, setHymn] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dropdownFilter, setDropdownFilter] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);

  // Editor modal state
  const [editorOpen, setEditorOpen] = useState(false);
  const [isNewContent, setIsNewContent] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const apiURL = process.env.REACT_APP_API_URL || '/api';

  // Load hymns directly from DB
  const loadHymns = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiURL}/content?type=mahlet`);
      if (res.ok) {
        const json = await res.json();
        if (json.success && Array.isArray(json.data)) {
          const mapped = json.data.map(item => ({
            id: item.itemId || item._id,
            _id: item._id,
            title: item.title,
            content: item.content
          }));
          setHymnsList(mapped);
          setLoading(false);
          return;
        }
      }
    } catch (err) {
      console.error('API error for Mahlet:', err.message);
    }
    setHymnsList([]);
    setLoading(false);
  }, [apiURL]);

  useEffect(() => {
    loadHymns();
  }, [loadHymns]);

  // Filter hymns for dropdown based on filter text
  const filteredHymns = useMemo(() => {
    if (!dropdownFilter.trim()) return hymnsList;
    const lower = dropdownFilter.toLowerCase();
    return hymnsList.filter((item) =>
      item.title?.toLowerCase().includes(lower)
    );
  }, [dropdownFilter, hymnsList]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDropdown && !event.target.closest('.custom-select-wrapper')) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown]);

  // Handle selection change
  useEffect(() => {
    if (selected) {
      const found = hymnsList.find((item) => (item.id || item._id).toString() === selected);
      setHymn(found || null);
    } else {
      setHymn(null);
    }
  }, [selected, hymnsList]);

  // Handle search
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setSearchResults([]);
      return;
    }

    setLoading(true);
    const lower = searchTerm.toLowerCase();
    const results = [];

    hymnsList.forEach((item) => {
      if (item.title?.toLowerCase().includes(lower)) {
        results.push({
          id: item.id || item._id,
          title: item.title,
          snippet: 'በአርዕስቱ ውስጥ ተገኝቷል'
        });
        return;
      }

      if (Array.isArray(item.content)) {
        item.content.forEach((section) => {
          if (section.wazema && section.wazema.toLowerCase().includes(lower)) {
            results.push({
              id: item.id || item._id,
              title: item.title,
              snippet: section.wazema.substring(0, 100) + '...'
            });
            return;
          }

          if (Array.isArray(section.contain)) {
            section.contain.forEach((subSection) => {
              if (subSection.title && subSection.title.toLowerCase().includes(lower)) {
                results.push({
                  id: item.id || item._id,
                  title: item.title,
                  snippet: `${subSection.title}: ${subSection.main?.substring(0, 80)}...`
                });
                return;
              }
              if (subSection.main && subSection.main.toLowerCase().includes(lower)) {
                results.push({
                  id: item.id || item._id,
                  title: item.title,
                  snippet: subSection.main.substring(0, 100) + '...'
                });
              }
            });
          }
        });
      }
    });

    setSearchResults(results);
    setLoading(false);
  }, [searchTerm, hymnsList]);

  const handleSearchResultClick = (result) => {
    setSelected(result.id.toString());
    setSearchTerm('');
    setSearchResults([]);
  };

  const canEditContent = user?.canEdit || user?.canUpdate || user?.isAdmin;
  const canInsertContent = user?.canInsert || user?.isAdmin;

  return (
    <div className="page-container mahlet-theme max-w-7xl mx-auto p-4 sm:p-6">
      <div className="page-header text-center mb-6">
        <h1 className="page-main-title text-3xl font-extrabold text-gray-900 mb-2">🎵 ሥርዓተ ማኅሌት</h1>
        <div className="page-subtitle text-gray-600">Order of Mahlet - የማኅሌት ሥርዓት</div>
        <div className="mt-2 flex items-center justify-center gap-2">
          <span className="text-xs px-2.5 py-1 rounded-full bg-purple-100 text-purple-800 font-medium">
            ⚡ ከመረጃ ቋት (MongoDB) የተገኘ
          </span>
          {canEditContent && (
            <span className="text-xs px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800 font-medium flex items-center gap-1">
              <span>👤</span> የተፈቀደ አርታዒ ({user?.email})
            </span>
          )}
        </div>
      </div>

      <div className="controls-container bg-white rounded-xl shadow-md p-4 sm:p-6 mb-6 border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="control-group">
            <label className="block text-xs font-bold text-gray-700 mb-1">📅 ማኅሌት ይምረጡ (Select Hymn)</label>
            <div className="custom-select-wrapper relative">
              <input
                type="text"
                placeholder="ለምሳሌ፡- መስከረም፣ ጥቅምት ወይም በዓል..."
                value={dropdownFilter}
                onChange={(e) => setDropdownFilter(e.target.value)}
                onFocus={() => setShowDropdown(true)}
                className="w-full px-3 py-2 text-sm border rounded-lg border-gray-300 focus:ring-2 focus:ring-purple-500 outline-none"
              />
              {showDropdown && (
                <div className="absolute top-full left-0 right-0 bg-white border border-gray-300 rounded-lg shadow-lg z-20 mt-1 max-h-60 overflow-y-auto">
                  <div className="flex justify-between items-center p-2.5 border-b border-gray-100 bg-gray-50 text-xs font-semibold text-gray-600">
                    <span>{filteredHymns.length} ማኅሌቶች</span>
                    <button onClick={() => setShowDropdown(false)} className="text-gray-400 hover:text-gray-600">✕</button>
                  </div>
                  {filteredHymns.map((item) => (
                    <div
                      key={item.id || item._id}
                      className={`p-3 hover:bg-purple-50 cursor-pointer border-b border-gray-100 text-xs sm:text-sm ${selected === (item.id || item._id).toString() ? 'bg-purple-100 text-purple-800 font-bold' : ''}`}
                      onClick={() => {
                        setSelected((item.id || item._id).toString());
                        setShowDropdown(false);
                      }}
                    >
                      {item.title}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="control-group">
            <label className="block text-xs font-bold text-gray-700 mb-1">🔍 ፈልግ (Search)</label>
            <input
              type="text"
              placeholder="በማኅሌቱ ይዘት ውስጥ ይፈልጉ..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 text-sm border rounded-lg border-gray-300 focus:ring-2 focus:ring-purple-500 outline-none"
            />
          </div>
        </div>

        {canInsertContent && (
          <div className="mt-4 pt-3 border-t border-gray-100 flex justify-end">
            <button
              onClick={() => {
                setIsNewContent(true);
                setEditingItem({ contentType: 'mahlet', title: '', content: [] });
                setEditorOpen(true);
              }}
              className="px-3.5 py-1.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white text-xs font-semibold rounded-lg shadow transition flex items-center gap-1.5"
            >
              <span>➕</span> አዲስ ማኅሌት ወደ DB አስገባ
            </button>
          </div>
        )}
      </div>

      {searchResults.length > 0 && (
        <div className="bg-white rounded-xl shadow p-4 mb-6 border border-gray-200">
          <h3 className="font-bold text-base mb-3">የፍለጋ ውጤቶች ({searchResults.length})</h3>
          <div className="space-y-2">
            {searchResults.map((r, i) => (
              <div
                key={i}
                onClick={() => handleSearchResultClick(r)}
                className="p-3 border rounded-lg hover:bg-purple-50 cursor-pointer transition text-sm flex items-center justify-between"
              >
                <div>
                  <span className="font-bold text-purple-700">{r.title}</span>
                  <p className="text-xs text-gray-600 mt-0.5">{r.snippet}</p>
                </div>
                <span className="text-xs text-purple-600">ይመልከቱ →</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-8 text-gray-500">ማኅሌት ከመረጃ ቋት እየተጫነ ነው...</div>
      ) : hymn ? (
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 space-y-6">
          <div className="border-b border-gray-200 pb-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <h2 className="text-2xl font-bold text-gray-800">{hymn.title}</h2>
            {canEditContent && (
              <button
                onClick={() => {
                  setIsNewContent(false);
                  setEditingItem({ ...hymn, contentType: 'mahlet' });
                  setEditorOpen(true);
                }}
                className="px-3.5 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-xs font-semibold shadow transition flex items-center gap-1.5"
              >
                <span>✏️</span> ይህን ማኅሌት አርትዕ (Edit in DB)
              </button>
            )}
          </div>

          <div className="space-y-6">
            {Array.isArray(hymn.content) && hymn.content.map((sec, idx) => (
              <div key={idx} className="border border-purple-100 rounded-xl overflow-hidden bg-purple-50/20">
                {sec.wazema && (
                  <div className="bg-purple-600 text-white p-3 font-bold text-base">
                    {sec.wazema}
                  </div>
                )}
                {sec.title && (
                  <div className="bg-indigo-600 text-white p-3 font-bold text-base">
                    {sec.title}
                  </div>
                )}
                <div className="p-4 space-y-4">
                  {Array.isArray(sec.contain) ? (
                    sec.contain.map((sub, sIdx) => (
                      <div key={sIdx} className="border-b border-gray-100 pb-3 last:border-b-0">
                        {sub.title && <h4 className="font-bold text-amber-800 text-sm mb-1">{sub.title}</h4>}
                        <p className="text-gray-800 text-sm sm:text-base leading-relaxed whitespace-pre-wrap">
                          {typeof sub.main === 'string' ? sub.main.replace(/\\n/g, '\n') : sub.main}
                        </p>
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-800 text-sm sm:text-base leading-relaxed whitespace-pre-wrap">
                      {typeof sec.main === 'string' ? sec.main.replace(/\\n/g, '\n') : (sec.main || JSON.stringify(sec))}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 pt-4 border-t border-gray-200">
            <SocialActions
              contentId={`mahlet_${hymn.id || hymn._id}`}
              contentType="mahlet"
              title={hymn.title}
              content={typeof hymn.content === 'string' ? hymn.content : JSON.stringify(hymn.content).substring(0, 200)}
              showBookmark={true}
              showShare={true}
              showLike={true}
            />
          </div>
        </div>
      ) : (
        !searchTerm && (
          <div className="text-center py-12 bg-white rounded-xl shadow border border-gray-200 text-gray-500">
            <div className="text-3xl mb-2">🎵</div>
            <p className="font-semibold">እባክዎ ከላይ ማኅሌት ይምረጡ ወይም ይፈልጉ</p>
          </div>
        )
      )}

      <ContentEditorModal
        isOpen={editorOpen}
        onClose={() => setEditorOpen(false)}
        contentType="mahlet"
        initialData={editingItem}
        isNew={isNewContent}
        onSaved={() => loadHymns()}
      />
    </div>
  );
}

export default Mahlet;
